/**
 * helpers/format.ts — shared number / currency / percent formatters.
 *
 * Phase 2 introduces the analytics dashboard. To keep numbers consistent
 * across every page (KPI tile / chart tooltip / table cell), every TSX file
 * goes through these helpers — never call `.toFixed()` ad-hoc.
 *
 * Conventions (PHASE2_SPEC §3):
 *   - Money in ₹ Crore, prefix `₹ `, default 1 dp (2 dp for KPI tiles).
 *   - Counts use comma-thousands.
 *   - Percentages are 1 dp by default and append `%`.
 */

/** Indian-locale comma grouping. Falls back to en-US which produces the same
 * digit grouping for integers up to 6 digits, but `en-IN` is the spec. */
const NUM_LOCALE = 'en-IN';

function isFiniteNumber(n: unknown): n is number {
	return typeof n === 'number' && Number.isFinite(n);
}

/** Returns `"₹ 7,776.2 cr"`. Negative inputs are formatted as `"-₹ 12.4 cr"`. */
export function formatCrore(n: number | null | undefined, dp: number = 1): string {
	if (!isFiniteNumber(n)) return '—';
	const abs = Math.abs(n);
	const sign = n < 0 ? '-' : '';
	return `${sign}₹ ${abs.toLocaleString(NUM_LOCALE, {
		minimumFractionDigits: dp,
		maximumFractionDigits: dp,
	})} cr`;
}

/** Like formatCrore, but without the trailing unit. Used inside chart axes. */
export function formatCroreCompact(n: number | null | undefined, dp: number = 1): string {
	if (!isFiniteNumber(n)) return '—';
	return n.toLocaleString(NUM_LOCALE, {
		minimumFractionDigits: dp,
		maximumFractionDigits: dp,
	});
}

/** Comma-thousands integer. NULL / NaN ⇒ `"—"`. */
export function formatCount(n: number | null | undefined): string {
	if (!isFiniteNumber(n)) return '—';
	return Math.round(n).toLocaleString(NUM_LOCALE);
}

/** Percent with `%` suffix. `formatPct(4.9)` → `"4.9%"`. */
export function formatPct(n: number | null | undefined, dp: number = 1): string {
	if (!isFiniteNumber(n)) return '—';
	return `${n.toLocaleString(NUM_LOCALE, {
		minimumFractionDigits: dp,
		maximumFractionDigits: dp,
	})}%`;
}

/** Compact short form for KPI tiles ("7.78K cr", etc.). Currently delegates
 * to formatCrore — the helper is here so future axes / sparkline labels have
 * a single hook for compact rendering. */
export function formatCroreShort(n: number | null | undefined, dp: number = 1): string {
	return formatCrore(n, dp);
}

/** Always-positive crore (loan amounts come back negative — see spec §1). */
export function formatAbsCrore(n: number | null | undefined, dp: number = 1): string {
	if (!isFiniteNumber(n)) return '—';
	return formatCrore(Math.abs(n), dp);
}

/** Compute NPA % live. Both inputs must be ABS; returns 0 when loan is 0. */
export function computeNpaPct(npaCr: number | null | undefined, loanCr: number | null | undefined): number {
	if (!isFiniteNumber(npaCr) || !isFiniteNumber(loanCr) || loanCr === 0) return 0;
	return (Math.abs(npaCr) * 100) / Math.abs(loanCr);
}
